// Minimal browser DOM bindings. All ambient (no `from` clause) — document,
// Element, and Event genuinely exist as globals in a browser, no import
// needed. Member names use the real JS casing exactly (camelCase), since
// extern declarations describe an existing external contract rather than
// idiomatic KopScript code — there's no per-member rename mechanism. `extern
// class` declarations end in `;`, like the other two extern forms.

extern class Event {
  Element target { get; }
  void preventDefault();
};

extern class Element {
  string textContent { get; set; }
  string id { get; set; }
  string className { get; set; }
  // The one property the vdom patch engine (src/vdom.ks) always sets via
  // direct property assignment, never setAttribute — setAttribute("value",
  // x) sets the DEFAULT value attribute, not the current live one, a real
  // DOM footgun (and the exact property behind the original typing bug
  // this whole diffing effort traces back to).
  string value { get; set; }
  // Only ever assigned by the vdom patch engine for a VElement.RawHtml leaf
  // — never diffed into, an opaque blob the same way `raw string` is.
  string innerHTML { get; set; }
  void appendChild(Element child);
  void replaceChild(Element newChild, Element oldChild);
  // `referenceChild` is nullable — real DOM insertBefore(node, null) means
  // "append at the end," used by the patch engine's child-reordering step.
  void insertBefore(Element newChild, Element? referenceChild);
  void removeChild(Element child);
  // The generic escape hatch VElement.SetAttr's ExtraNames/ExtraValues
  // patch through — real HTML attributes only (href, src, alt,
  // placeholder, ...), never `value` (see above).
  void setAttribute(string name, string value);
  void removeAttribute(string name);
  bool disabled { get; set; }
  bool checked { get; set; }
  void addEventListener(string eventType, (Event) => void handler);
  void removeEventListener(string eventType, (Event) => void handler);
};

extern class Document {
  Element createElement(string tagName);
  Element getElementById(string id);
  Element body { get; }
  // Where ScopedStyles.Inject (vdom.ks) puts a component type's own
  // rewritten <style> — a scoped stylesheet is global infrastructure
  // (injected once per type, never removed), not page content, so it
  // belongs in <head>, not wherever a given instance happens to mount.
  Element head { get; }
};

extern class Location {
  string pathname { get; }
  // The real query string including its leading "?" (e.g. "?sort=name"),
  // or "" if the current URL has none — see Router.ParseQuery/Query.
  string search { get; }
};

extern class History {
  // Param named `historyState`, not `state` — `state` is a KopScript
  // keyword (state<T>), not a valid parameter name.
  void pushState(string historyState, string title, string url);
};

extern class Window {
  void addEventListener(string eventType, (Event) => void handler);
};

extern Document document;
extern Location location;
extern History history;
extern Window window;
